import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../Components/ui/card";
import { Eye, EyeClosed, KeyRound, Mail, User, Building2, Phone, Globe } from "lucide-react";

function Register() {
    // state variables for user information
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    
    // state variables for company information
    const [companyName, setCompanyName] = useState("");
    const [companySize, setCompanySize] = useState("1-10");
    const [industry, setIndustry] = useState("");
    const [country, setCountry] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [town, setTown] = useState("");
    const [region, setRegion] = useState("");
    const [postcode, setPostcode] = useState("");
    
    // state variables for subscription
    const [planType, setPlanType] = useState("Trial");
    
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // state variable for error messages
    const [error, setError] = useState("");

    const errorMsgStyle = "bg-red-500 p-3 text-white text-center font-bold rounded";
   // const successMsgStyle = "bg-green-500 p-3 text-white text-center font-bold rounded";
    const [messageStyle, setMessageStyle] = useState(errorMsgStyle);

    const handleLoginClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        navigate("/login");
    }

    const handleShowPasswordClick = () => {
        setShowPassword(!showPassword); // toggle show password
    }

    // handle change events for input fields
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === "email") setEmail(value);
        if (name === "password") setPassword(value);
        if (name === "confirmPassword") setConfirmPassword(value);
        if (name === "firstName") setFirstName(value);
        if (name === "lastName") setLastName(value);
        if (name === "phoneNumber") setPhoneNumber(value);
        if (name === "companyName") setCompanyName(value);
        if (name === "companySize") setCompanySize(value);
        if (name === "industry") setIndustry(value);
        if (name === "country") setCountry(value);
        if (name === "address1") setAddress1(value);
        if (name === "address2") setAddress2(value);
        if (name === "town") setTown(value);
        if (name === "region") setRegion(value);
        if (name === "postcode") setPostcode(value);
        if (name === "planType") setPlanType(value);
    };

    // handle submit event for the form
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        // validate all required fields
        if (!email || !password || !confirmPassword || !firstName || !lastName || 
            !companyName || !industry || !country) {
            setError("Please fill in all required fields");
            return;
        }
        
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError("Please enter a valid email address");
            return;
        }
        
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        
        // Password validation to match ASP.NET Identity requirements
        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        
        if (!/[A-Z]/.test(password)) {
            setError("Password must contain at least one uppercase letter");
            return;
        }
        
        if (!/[a-z]/.test(password)) {
            setError("Password must contain at least one lowercase letter");
            return;
        }
        
        if (!/[0-9]/.test(password)) {
            setError("Password must contain at least one number");
            return;
        }
        
        if (!/[^a-zA-Z0-9]/.test(password)) {
            setError("Password must contain at least one special character (!@#$%^&* etc.)");
            return;
        }

        // clear error message and prepare data
        setError("");
        setIsLoading(true);
        
        const registrationData = {
            email,
            password,
            firstName,
            lastName,
            phoneNumber,
            companyName,
            companySize,
            industry,
            country,
            address1,
            address2,
            town,
            region,
            postcode,
            planType
        };

        fetch("/Account/register-tenant", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(registrationData),
        }).then(async (response) => {
            if (response.ok) {
                const data = await response.json();
                // Redirect to welcome page immediately with tenant info and email
                navigate(`/welcome?tenantId=${data.tenantId}&companyName=${encodeURIComponent(data.companyName)}&email=${encodeURIComponent(email)}`);
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Error registering tenant. Please try again.");
                setMessageStyle(errorMsgStyle);
            }
        }).catch((error) => {
            console.error(error);
            setError("Network error. Please check your connection and try again.");
            setMessageStyle(errorMsgStyle);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    return (
        <div className="flex flex-row min-h-screen justify-center items-center bg-gradient-to-br from-background to-secondary/20">
            <Card className="flex flex-col justify-self-center p-8 max-w-2xl">
            <h3 className="mb-6 text-3xl font-bold text-center">Register Your Company</h3>

            <form className="w-full" onSubmit={handleSubmit}>
                {/* User Information Section */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-gray-300">User Information</h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="firstName"><User /></label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                placeholder="First Name *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="lastName"><User /></label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                placeholder="Last Name *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="email"><Mail /></label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="Email Address *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="password"><KeyRound /></label>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                placeholder="Password *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={password}
                                onChange={handleChange}
                                required
                            />
                            <div className="content-center bg-gray-200 text-gray-500 p-2 cursor-pointer" onClick={handleShowPasswordClick}>{showPassword ? <Eye /> : <EyeClosed />}</div>
                        </div>
                    </div>
                    <div className="mb-4 text-xs text-gray-500 -mt-2 pl-1">
                        Password must contain: 6+ characters, uppercase, lowercase, number, and special character (!@#$%^&*)
                    </div>

                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="confirmPassword"><KeyRound /></label>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Confirm Password *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={confirmPassword}
                                onChange={handleChange}
                                required
                            />
                            <div className="content-center bg-gray-200 text-gray-500 p-2 cursor-pointer" onClick={handleShowPasswordClick}>{showPassword ? <Eye /> : <EyeClosed />}</div>
                        </div>
                    </div>
                </div>

                {/* Company Information Section */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-gray-300">Company Information</h4>
                    
                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="companyName"><Building2 /></label>
                            <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                placeholder="Company Name *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={companyName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="phoneNumber"><Phone /></label>
                            <input
                                type="tel"
                                id="phoneNumber"
                                name="phoneNumber"
                                placeholder="Phone Number (Optional)"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={phoneNumber}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex flex-col">
                            <label className="text-gray-300 text-sm font-bold mb-2" htmlFor="companySize">Company Size</label>
                            <select
                                id="companySize"
                                name="companySize"
                                className="bg-gray-200 border-2 border-gray-200 rounded py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={companySize}
                                onChange={handleChange}
                            >
                                <option value="1-10">1-10 employees</option>
                                <option value="11-50">11-50 employees</option>
                                <option value="51-200">51-200 employees</option>
                                <option value="201-500">201-500 employees</option>
                                <option value="501+">501+ employees</option>
                            </select>
                        </div>
                        
                        <div className="flex flex-col">
                            <label className="text-gray-300 text-sm font-bold mb-2" htmlFor="industry">Industry *</label>
                            <input
                                type="text"
                                id="industry"
                                name="industry"
                                placeholder="e.g., Technology, Finance"
                                className="bg-gray-200 border-2 border-gray-200 rounded py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={industry}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="country"><Globe /></label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                placeholder="Country *"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={country}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Address Fields */}
                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="address1"><Building2 /></label>
                            <input
                                type="text"
                                id="address1"
                                name="address1"
                                placeholder="Address Line 1"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={address1}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="md:flex md:items-center mb-4">
                        <div className="flex flex-row w-full bg-gray-200 rounded">
                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="address2"><Building2 /></label>
                            <input
                                type="text"
                                id="address2"
                                name="address2"
                                placeholder="Address Line 2"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={address2}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="flex flex-col">
                            <input
                                type="text"
                                id="town"
                                name="town"
                                placeholder="Town/City"
                                className="bg-gray-200 border-2 border-gray-200 rounded py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={town}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="flex flex-col">
                            <input
                                type="text"
                                id="region"
                                name="region"
                                placeholder="State/Region"
                                className="bg-gray-200 border-2 border-gray-200 rounded py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={region}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="flex flex-col">
                            <input
                                type="text"
                                id="postcode"
                                name="postcode"
                                placeholder="Postcode"
                                className="bg-gray-200 border-2 border-gray-200 rounded py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                value={postcode}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Subscription Plan Section */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-gray-300">Subscription Plan</h4>
                    
                    <div className="flex flex-col">
                        <label className="text-gray-300 text-sm font-bold mb-2" htmlFor="planType">Select Plan</label>
                        <select
                            id="planType"
                            name="planType"
                            className="bg-gray-200 border-2 border-gray-200 rounded py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                            value={planType}
                            onChange={handleChange}
                        >
                            <option value="Trial">Trial (14 days free)</option>
                            <option value="Basic">Basic Plan</option>
                            <option value="Professional">Professional Plan</option>
                            <option value="Enterprise">Enterprise Plan</option>
                        </select>
                    </div>
                </div>

                <div className="md:flex md:items-center">
                    <button 
                        className="mb-4 w-full button-default" 
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? "Creating Your Account..." : "Register Company"}
                    </button>
                </div>
                <div className="md:flex md:items-center">
                    <button 
                        className="mb-4 w-full shadow bg-gray-600 hover:bg-gray-400 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-4 rounded" 
                        onClick={handleLoginClick}
                        type="button"
                    >
                        Already have an account? Login
                    </button>
                </div>
            </form>
                {error && <p className={messageStyle}>{error}</p>}
                {isLoading && <div className="flex flex-row" ><div className="flex flex-col m-auto"><span className="loader"></span></div></div>}
            </Card>
        </div>
    );
}

export default Register;
